/**
 * Protected Playbooks Route
 *
 * Restricts access to Production Playbooks to users with 'creator' or 'admin' role.
 * Regular users see a friendly message explaining the feature is for creators.
 */

import React from 'react';
import { Redirect, Link, useLocation } from 'wouter';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { BookOpen, Lock, Sparkles } from 'lucide-react';
import { SmartLogin } from '../auth/SmartLogin';

interface ProtectedPlaybooksRouteProps {
  children: React.ReactNode;
}

export const ProtectedPlaybooksRoute: React.FC<ProtectedPlaybooksRouteProps> = ({ children }) => {
  const { user, loading, sessionReady, userRole } = useSupabaseAuth();
  const [location] = useLocation();

  // Show loading state while checking auth OR waiting for session to be ready
  if (loading || !sessionReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">
            {loading ? 'Checking access...' : 'Initializing session...'}
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated (only after session is ready)
  // Pass the current location so SmartLogin can redirect back after auth
  if (!user) {
    return <SmartLogin returnTo={location} />;
  }

  // Only creators and admins can access playbooks
  const hasAccess = userRole === 'creator' || userRole === 'admin';

  // If user doesn't have access, show friendly message with debugging info
  if (!hasAccess) {
    // Add debugging info in development
    console.log('[ProtectedPlaybooksRoute] Access denied:');
    console.log('  User email:', user?.email);
    console.log('  User ID:', user?.id);
    console.log('  Current role:', userRole);
    console.log('  Required roles: creator or admin');

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-indigo-100 rounded-full p-4">
              <Lock className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Production Playbooks</h1>
              <p className="text-indigo-600 font-semibold">Creator Feature</p>
            </div>
          </div>

          {/* Debugging info card (only visible in dev mode) */}
          {import.meta.env.DEV && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-mono text-yellow-800">
                <strong>Debug Info:</strong><br />
                Email: {user?.email}<br />
                User ID: {user?.id}<br />
                Current Role: <span className="font-bold">{userRole}</span><br />
                Required: <span className="font-bold">creator</span> or <span className="font-bold">admin</span><br />
                <br />
                <strong>Fix:</strong> Update user_roles array in profiles table
              </p>
            </div>
          )}

          <div className="space-y-4 mb-6">
            <p className="text-lg text-gray-700">
              Production Playbooks are advanced AI-native cultural innovation patterns designed for
              creators who are producing workshops, events, and community experiences.
            </p>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-600" />
                What are Production Playbooks?
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 font-bold">•</span>
                  <span>Document complete production workflows (musicals, biopics, story services)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 font-bold">•</span>
                  <span>Capture AI-native patterns for community replication</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 font-bold">•</span>
                  <span>Track event logistics, recovery strategies, and breakthrough moments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 font-bold">•</span>
                  <span>Enable workshop creation and event management</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Become a Creator
              </h3>
              <p className="text-gray-700 mb-3">
                Creators have access to advanced tools for producing transformative community experiences.
                If you're interested in creating workshops, events, or cultural productions, reach out to
                learn more about creator access.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Go Back
            </button>
            <Link href="/request-creator-access">
              <a className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-colors text-center flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5" />
                Request Creator Access
              </a>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // User has access, render children
  return <>{children}</>;
};
